<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useEditTransaction } from "@/components/EditTransaction/state.ts";
import { isOn } from "@/libs/features.ts";

const state = useEditTransaction();

const { applyToIncomeTax, name, settlesReservation, type } = storeToRefs(state);

// A tax payment settles the reserve; it must not also be a deductible flagged expense.
const onApplyToIncomeTax = (checked: boolean | null | undefined) => {
  state.applyToIncomeTax = Boolean(checked);
  if (state.applyToIncomeTax) {
    state.settlesReservation = null;
  }
};

const onSettlesReservation = (checked: boolean | null | undefined) => {
  state.settlesReservation = checked ? "income_tax" : null;
  if (state.settlesReservation) {
    state.applyToIncomeTax = false;
  }
};
</script>

<template>
  <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
    <label class="block text-sm/6 font-medium text-gray-900" for="name">Naam</label>
    <div class="flex flex-col space-y-1.5 sm:col-span-2">
      <div
        class="flex items-center rounded-md bg-white pl-3 outline -outline-offset-1 outline-gray-300 focus-within:outline focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:max-w-md"
      >
        <div class="shrink-0 select-none text-base text-gray-500 sm:text-sm/6"></div>
        <input
          id="name"
          v-model="name"
          class="block min-w-0 grow py-1.5 pl-1 pr-3 text-base text-gray-900 placeholder:text-gray-400 outline-0 sm:text-sm/6"
          name="name"
          type="text"
        />
      </div>

      <div v-if="isOn('income-tax')" class="space-y-5 sm:col-span-2">
        <div class="flex gap-3">
          <div class="flex h-6 shrink-0 items-center">
            <div class="group grid size-4 grid-cols-1">
              <input
                id="comments"
                :checked="applyToIncomeTax"
                aria-describedby="comments-description"
                class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                name="comments"
                type="checkbox"
                @change="onApplyToIncomeTax(($event.target as HTMLInputElement).checked)"
              />
              <svg
                class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  class="opacity-0 group-has-checked:opacity-100"
                  d="M3 8L6 11L11 3.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
                <path
                  class="opacity-0 group-has-indeterminate:opacity-100"
                  d="M3 7H11"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
            </div>
          </div>
          <div class="text-sm/6">
            <label class="font-medium text-gray-900" for="comments"
              >Telt mee voor inkomstenbelasting</label
            >
          </div>
        </div>

        <div v-if="type === 'expense'" class="flex gap-3">
          <div class="flex h-6 shrink-0 items-center">
            <div class="group grid size-4 grid-cols-1">
              <input
                id="settles-reservation"
                :checked="settlesReservation === 'income_tax'"
                class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                name="settles-reservation"
                type="checkbox"
                @change="onSettlesReservation(($event.target as HTMLInputElement).checked)"
              />
              <svg
                class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  class="opacity-0 group-has-checked:opacity-100"
                  d="M3 8L6 11L11 3.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
                <path
                  class="opacity-0 group-has-indeterminate:opacity-100"
                  d="M3 7H11"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
            </div>
          </div>
          <div class="text-sm/6">
            <label class="font-medium text-gray-900" for="settles-reservation"
              >Dit is een betaling van inkomstenbelasting</label
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
